import { useEffect, useState } from "react";
import { setMixpanelProperties } from "../services/mixpanel/mixpanel";

interface ABTestConfig<T> {
  testName: string;
  variants: {
    [key: string]: T; // map variant keys to values of the AB (Ex: different text, colors, etc)
  };
  weights?: {
    [key: string]: number;
  };
}

/**
 * A hook for implementing A/B testing functionality.
 * 
 * @template T The type of values associated with each variant
 * @param {Object} config The A/B test configuration
 * @param {string} config.testName A unique identifier for this A/B test
 * @param {Object.<string, T>} config.variants Map of variant keys to their associated values
 * @param {Object.<string, number>} [config.weights] Optional weights to control variant distribution
 * @returns {T | undefined} The selected variant value
 * 
 * @example
 * // Simple A/B test with equal weights
 * const buttonColor = useABTest({
 *   testName: 'button_color_test',
 *   variants: {
 *     control: '#1A73E8',
 *     variant: '#34A853'
 *   },
 *   weights: {
 *     control: 50,
 *     variant: 50
 *   }
 * });
 * 
 * @example
 * // A/B test with weighted variants
 * const pricingTest = useABTest({
 *   testName: 'pricing_test',
 *   variants: {
 *     control: { price: 99, period: 'yearly' },
 *     variantA: { price: 79, period: 'yearly' },
 *     variantB: { price: 9.99, period: 'monthly' }
 *   },
 *   weights: {
 *     control: 50,
 *     variantA: 25,
 *     variantB: 25
 *   }
 * });
 */

export function useABTest<T>({ testName, variants, weights }: ABTestConfig<T>) {
  const [variant, setVariant] = useState<T>();

  useEffect(() => {
    // Get stored variant or assign new one
    const storedVariant = localStorage.getItem(testName);
    if (storedVariant && variants[storedVariant]) {
      setVariant(variants[storedVariant]);
    } else {
      const variantKeys = Object.keys(variants);
      const selectedKey = selectVariant(variantKeys, weights);

      localStorage.setItem(testName, selectedKey);
      setVariant(variants[selectedKey]);

      setMixpanelProperties({ [testName]: selectedKey }, true);
    }
  }, [testName, variants, weights]);

  return variant;
}

function selectVariant(variants: string[], weights?: { [key: string]: number }): string {
  if (!weights) {
    return variants[Math.floor(Math.random() * variants.length)];
  }

  const totalWeight = Object.values(weights).reduce((sum, weight) => sum + weight, 0);
  let random = Math.random() * totalWeight;

  for (const variant of variants) {
    random -= weights[variant] || 0;
    if (random <= 0) return variant;
  }

  return variants[0];
}

export function getABTestVariant(testName: string) {
  return typeof window === "undefined" ? "control" : localStorage.getItem(testName);
}
